import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import texts from './texts'
import { getMe, isLoggedIn } from './lib/api'
import Landing from './pages/Landing'
import Login from './pages/Login'
import AuthCallback from './pages/AuthCallback'
import Home from './pages/Home'
import Games from './pages/Games'
import Me from './pages/Me'
import Stats from './pages/Stats'
import Rules from './pages/Rules'
import Privacy from './pages/Privacy'
import Share from './pages/Share'
import NewGame from './pages/NewGame'
import Game from './pages/Game'
import Clubs from './pages/Clubs'
import Club from './pages/Club'
import Join from './pages/Join'
import Admin from './pages/Admin'
import { ToastProvider } from './components/ui/Toast'

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => isLoggedIn())
  const {
    needRefresh: [needRefresh],
    updateServiceWorker
  } = useRegisterSW()

  useEffect(() => {
    // Et forældet mærke uden gyldig cookie ryddes af 401-interceptoren
    if (isLoggedIn()) {
      getMe().catch(() => setIsAuthenticated(false))
    }
  }, [])

  useEffect(() => {
    // Registrer et besøg én gang pr. browser-session
    const sessionKey = 'whistbuddy_visit_sent_v1'
    if (sessionStorage.getItem(sessionKey)) return
    sessionStorage.setItem(sessionKey, '1')

    fetch('/api/stats/visit', { method: 'POST' }).catch(() => {
      // Ingen handling - statistik må ikke blokere appen
    })
  }, [])

  const guard = (page: JSX.Element) => (isAuthenticated ? page : <Navigate to="/login" />)

  return (
    <ToastProvider>
      <Router>
        <Routes>
          <Route path="/" element={isAuthenticated ? <Home /> : <Landing />} />
          <Route path="/login" element={<Login setAuth={setIsAuthenticated} />} />
          <Route path="/auth/callback" element={<AuthCallback setAuth={setIsAuthenticated} />} />
          <Route path="/register" element={<Navigate to="/login" />} />
          <Route path="/regler" element={<Rules />} />
          <Route path="/privatliv" element={<Privacy />} />
          <Route path="/r/:token" element={<Share />} />
          <Route path="/me" element={guard(<Me />)} />
          <Route path="/spil" element={guard(<Games />)} />
          <Route path="/stats" element={guard(<Stats />)} />
          <Route path="/game/new" element={guard(<NewGame />)} />
          <Route path="/game/:gameId" element={guard(<Game />)} />
          <Route path="/klubber" element={guard(<Clubs />)} />
          <Route path="/klub/:id" element={guard(<Club />)} />
          <Route path="/j/:code" element={<Join setAuth={setIsAuthenticated} />} />
          <Route path="/admin" element={guard(<Admin />)} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
      {needRefresh && (
        <div className="fixed inset-x-4 bottom-[calc(16px+env(safe-area-inset-bottom,0px))] z-30 mx-auto flex max-w-[398px] items-center justify-between gap-3 rounded-2xl border border-text-2/20 bg-ink px-4 py-3 text-text shadow-[0_10px_24px_rgba(0,0,0,.35)]">
          <span className="text-[0.9em]">{texts.pwa.updateAvailable}</span>
          <button
            type="button"
            onClick={() => updateServiceWorker(true)}
            className="min-h-11 flex-none rounded-xl bg-gold px-4 font-bold text-ink"
          >
            {texts.pwa.reload}
          </button>
        </div>
      )}
    </ToastProvider>
  )
}

export default App
